import { CHAINS_SOCIALS } from '#lib/constants/chainsSocials.js'
import type {
	ChainScalabilityData,
	ChainId,
	ChainData,
	ChainDecentralizationData,
	ChainDevActivityData,
	ChainFinancialsData,
	Agent,
	CardActivity,
} from '#lib/models/index.js'
import { m } from '#lib/paraglide/messages.js'

import { formatCalculated, formatTxns } from './formatters'
import type { chainSchema } from './schema'

const getExplanation = (chain: ChainScalabilityData | ChainData) => {
	// @ts-expect-error checking dynamic key existence
	// eslint-disable-next-line import-x/namespace
	const explanation = m[`${chain.id}_tps_explanation`]

	return explanation != null
		? m.special_tps_explanation({
				explanation: explanation(),
			})
		: m.default_tps_explanation()
}

const isChainWithSpecialTxns = (
	chainId: ChainId
): chainId is 'icp_chain' | 'keeta_mainnet_chain' | 'keeta_testnet_chain' =>
	chainId === 'icp_chain' || chainId === 'keeta_mainnet_chain' || chainId === 'keeta_testnet_chain'

export const getTpsTooltip = (chain: ChainScalabilityData | ChainData) => {
	if (chain.tps == null) return null

	const explanation = getExplanation(chain)

	if (isChainWithSpecialTxns(chain.id)) return explanation

	const start = chain.tps?.start.toLocaleString('en-US')
	const finish = chain.tps?.finish.toLocaleString('en-US')

	if (chain.id === 'tezos_ecosystem_chain')
		return `${explanation}<br /><br />Registered in block window corresponding to Tezos blocks:<br />#${start} – #${finish}`

	return `${explanation}<br /><br />Registered in block window:<br />#${start} – #${finish}`
}

export const getTxnsTooltip = (chain: ChainScalabilityData | ChainData) => {
	if (chain.tps == null || chain.txns == null) return null

	const explanation = getExplanation(chain)
	const value = formatTxns(chain.txns)

	if (isChainWithSpecialTxns(chain.id))
		return m.special_txns_explanation({
			value,
			explanation,
		})

	const start = chain.tps?.start.toLocaleString('en-US')
	const finish = chain.tps?.finish.toLocaleString('en-US')

	if (chain.id === 'tezos_ecosystem_chain')
		return `Exact count: ${value}<br /><br />${explanation}<br /><br />Registered in block window corresponding to Tezos blocks:<br />#${start} – #${finish}`

	return `Exact count: ${value}<br /><br />${explanation}<br /><br />Registered in block window:<br />#${start} – #${finish}`
}

export const getTotalTxnsTooltip = (chain: ChainScalabilityData | ChainData) => {
	if (chain.totalTxns == null || chain.totalTxns.value == null || chain.totalTxns.block == null)
		return null

	const explanation = getExplanation(chain)
	const value = formatTxns(chain.totalTxns.value)

	if (isChainWithSpecialTxns(chain.id))
		return m.special_txns_explanation({
			value,
			explanation,
		})

	const block = chain.totalTxns?.block.toLocaleString('en-US')

	if (chain.id === 'tezos_ecosystem_chain')
		return `Exact count: ${value}<br /><br />${explanation}<br /><br />Calculated up to the block corresponding to Tezos block:<br />#${block}`

	return `Exact count: ${value}<br /><br />${explanation}<br /><br />Calculated up to block: #${block}`
}

export const isChainWithSpecialOneBlockMaxTps = (
	chainId: ChainId,
	state: ReturnType<typeof chainSchema.parse>['max-tps']
): chainId is
	| 'bitcoin_chain'
	| 'ethereum_chain'
	| 'cardano_chain'
	| 'waterfall_chain'
	| 'ecash_chain'
	| 'bsv_chain' =>
	state === 'one-block' &&
	(chainId === 'bitcoin_chain' ||
		chainId === 'ethereum_chain' ||
		chainId === 'cardano_chain' ||
		chainId === 'waterfall_chain' ||
		chainId === 'ecash_chain' ||
		chainId === 'bsv_chain')

export const getMaxTpsTooltip = (
	chain: ChainScalabilityData | ChainData,
	state: ReturnType<typeof chainSchema.parse>['max-tps']
) => {
	if (chain.maxTps == null) return null

	let explanation = getExplanation(chain)

	if (isChainWithSpecialTxns(chain.id)) return explanation

	if (state === 'hundred-blocks') {
		const start = chain.maxTps.block
		if (start == null) return null

		const finish = start + 99
		const startStr = start.toLocaleString('en-US')
		const finishStr = finish.toLocaleString('en-US')

		if (chain.id === 'tezos_ecosystem_chain')
			return `${explanation}<br /><br />Registered in block window corresponding to Tezos blocks:<br />#${startStr} – #${finishStr}`

		return `${explanation}<br /><br />Registered in block window:<br />#${startStr} – #${finishStr}`
	}

	if (isChainWithSpecialOneBlockMaxTps(chain.id, state)) {
		// eslint-disable-next-line import-x/namespace
		explanation = m[`${chain.id}_max_tps_explanation`]()
	}

	const block = (
		'maxTpsOneBlock' in chain ? chain.maxTpsOneBlock : chain.maxTps
	)?.block.toLocaleString('en-US')

	if (block == null) return null

	if (chain.id === 'tezos_ecosystem_chain')
		return `${explanation}<br /><br />Registered in the block corresponding to Tezos block:<br />#${block}`

	return `${explanation}<br /><br />Registered in block: #${block}`
}

export const getBlocktimeTooltip = (chain: ChainScalabilityData | ChainData) =>
	chain.blocktime != null && chain.id === 'hedera_chain'
		? m.hedera_chain_blocktime_explanation()
		: null

export const getLaunchedTooltip = (chain: ChainScalabilityData | ChainData) =>
	chain.ttf != null && chain.id === 'ecash_chain' ? m.ecash_chain_launch_date_explanation() : null

export const getGovernanceTooltip = (chain: ChainDecentralizationData | ChainData) => {
	switch (chain.governance) {
		case 'on_chain':
			return 'On-chain governance is a mechanism allowing decentralized communities to update a blockchain through direct on-chain voting.'
		case 'off_chain':
			return 'Off-chain governance is a form of blockchain governance where decisions are informally made off chain, (discussions typically happen in social media or forums). The result of discussions is based on the unanimous agreement among the community.'
		case 'council':
			return "Council governance is similar to the operational structure of boards of directors in traditional web2 companies. It involves a selected group of organizations or individuals responsible for determining the blockchain's future, strategy, and allocation of funds."
		case 'multisig':
			return 'Similar to the operational framework of private companies, this governance model involves a single entity empowered to independently adjust the core parameters of the blockchain, allocate funds, and redefine the strategy.'
		default:
			return null
	}
}

export const getNcTooltip = (chain: ChainDecentralizationData | ChainData) =>
	chain.nc != null ? m.tooltip_calculated({ date: formatCalculated(chain.decCalculated) }) : null

export const getValidatorsOrMinersTooltip = (chain: ChainDecentralizationData | ChainData) =>
	chain.validatorsOrMiners != null
		? m.tooltip_calculated({ date: formatCalculated(chain.decCalculated) })
		: null

export const getStakeOrHashrateTooltip = (chain: ChainDecentralizationData | ChainData) =>
	chain.stakeOrHashrate != null
		? m.tooltip_calculated({ date: formatCalculated(chain.decCalculated) })
		: null

const getGithubAnchor = (link: string) =>
	`<a href="${link}" target="_blank" rel="noopener noreferrer">${new URL(link).pathname.replace(/\//g, '')}</a>`

const getDevActivityTooltip = (chain: ChainDevActivityData | ChainData) => {
	const date = formatCalculated(chain.devCalculated)

	const socialLinks = CHAINS_SOCIALS[chain.id]

	if (socialLinks.github) {
		const orgs = Array.isArray(socialLinks.github)
			? `<ul class="list">${socialLinks.github.map(link => `<li>${getGithubAnchor(link)}</li>`).join('')}</ul>`
			: `<ul class="list"><li>${getGithubAnchor(socialLinks.github)}</li></ul>`

		return `The following organizations are tracked:${orgs}<br/>Last calculated on<br />${date}`
	}

	return m.tooltip_calculated({ date })
}

export const getDevelopersTooltip = (chain: ChainDevActivityData | ChainData) =>
	chain.developers != null ? getDevActivityTooltip(chain) : null

export const getReposTooltip = (chain: ChainDevActivityData | ChainData) =>
	chain.repos != null ? getDevActivityTooltip(chain) : null

export const getCommitsTooltip = (chain: ChainDevActivityData | ChainData) =>
	chain.commits != null ? getDevActivityTooltip(chain) : null

export const getStarsTooltip = (chain: ChainDevActivityData | ChainData) =>
	chain.stars != null ? getDevActivityTooltip(chain) : null

export const getWatchersTooltip = (chain: ChainDevActivityData | ChainData) =>
	chain.watchers != null ? getDevActivityTooltip(chain) : null

export const getRevenueTooltip = (chain: ChainFinancialsData | ChainData) =>
	chain.revenue?.value != null
		? m.tooltip_calculated({ date: formatCalculated(chain.financialsCalculated) })
		: null

export const getAvgTxFeeTooltip = (chain: ChainFinancialsData | ChainData) =>
	chain.avgTxFee?.value != null
		? m.tooltip_calculated({ date: formatCalculated(chain.financialsCalculated) })
		: null

export const getPriceTooltip = (chain: ChainFinancialsData | ChainData) =>
	chain.price?.value != null
		? m.tooltip_calculated({ date: formatCalculated(chain.financialsCalculated) })
		: null

export const getMarketCapTooltip = (chain: ChainFinancialsData | ChainData) =>
	chain.marketCap?.value != null
		? m.tooltip_calculated({ date: formatCalculated(chain.financialsCalculated) })
		: null

export const getFdvTooltip = (chain: ChainFinancialsData | ChainData) =>
	chain.fdv?.value != null
		? m.tooltip_calculated({ date: formatCalculated(chain.financialsCalculated) })
		: null

export const getMarketCapToFdvTooltip = (chain: ChainFinancialsData | ChainData) =>
	chain.marketCapToFdv?.value != null
		? m.tooltip_calculated({ date: formatCalculated(chain.financialsCalculated) })
		: null

export const getAgentCreatedTooltip = (agent: Agent) =>
	`${formatCalculated(agent.created)}<br />Block: #${agent.block}`

export const getCardTxnsTooltip = (card: CardActivity) => {
	const date = formatCalculated(card.calculated)

	switch (card.id) {
		case 'etherfi_card':
			return m.tooltip_card_calculated({ chain: 'Optimism', date })
		case 'metamask_card':
			return m.tooltip_card_calculated({ chain: 'Linea', date })
		case 'kast_card':
		case 'nexo_card':
		case 'redotpay_card':
			return m.tooltip_card_calculated({ chain: 'Ethereum', date })
		default:
			return m.tooltip_calculated({ date })
	}
}
